import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "~/components/Button";
import { SelectInput } from "~/components/SelectInput";
import { TextInput } from "~/components/TextInput";
import type { AdminUser } from "~/modules/authentication/types";
import type { Option } from "~/types/types";

import { type CreateAdminFormType, schema } from "./schema";

type Props = {
	defaultValues?: CreateAdminFormType;
	onSubmit(payload: CreateAdminFormType): Promise<void>;
};

const roleOptions: Option<AdminUser["role"]>[] = [
	{ label: "Owner (Integrafácil)", value: "owner" },
	{ label: "Client (Administradores de clientes)", value: "client" },
];

export const CreateAdminForm: React.FC<Props> = ({
	defaultValues,
	onSubmit,
}) => {
	const {
		control,
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<CreateAdminFormType>({
		resolver: zodResolver(schema),
		defaultValues,
	});

	const submit = useCallback(
		async (payload: CreateAdminFormType) => {
			await onSubmit(payload);
		},
		[onSubmit],
	);

	return (
		<form
			className="flex-1 flex flex-col gap-4"
			onSubmit={handleSubmit(submit)}
		>
			<TextInput
				label="Nome"
				placeholder="Nome do administrador"
				{...register("name")}
				error={errors.name?.message}
			/>
			<TextInput
				label="Email"
				placeholder="E-mail do administrador"
				inputMode="email"
				{...register("email")}
				error={errors.email?.message}
			/>
			<TextInput
				label="Senha"
				placeholder="Senha do administrador"
				type="password"
				{...register("password")}
				error={errors.password?.message}
			/>

			<Controller
				control={control}
				name="role"
				render={({ field }) => (
					<SelectInput
						label="Nível de autoridade"
						placeholder="Membros da Integrafácil ou clientes do sistema"
						value={field.value}
						options={roleOptions}
						onChange={field.onChange}
						error={errors.role?.message}
					/>
				)}
			/>
			<Button
				title="Cadastrar"
				loading={isSubmitting}
				loadingMessage="Atualizando..."
			>
				<span>Cadastrar</span>
			</Button>
		</form>
	);
};
